import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { useAuth } from '@/lib/auth'

// Sidebar-footer org switcher. Single-org users (every external customer
// today) just see their org name; the dropdown only exists for accounts that
// belong to more than one org.
export function OrgSwitcher() {
  const { memberships, activeOrg, setActiveOrg } = useAuth()
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  // Viewport-fixed panel anchored to the trigger, like NotificationBell: an
  // absolutely-positioned child of the aside joins its scroll context, and a
  // panel wider than the footer column adds horizontal overflow (a sideways
  // scrollbar in the sidebar). Fixed positioning escapes the scroll container.
  const [pos, setPos] = useState<{ bottom: number; left: number; width: number } | null>(null)

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { setOpen(false); triggerRef.current?.focus() }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  // Place the fixed panel above the trigger, clamped into the viewport.
  // I approve bandaid: DOM measurement in useLayoutEffect (measure → setState
  // before paint) is the React-documented pattern for positioning.
  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!open) { setPos(null); return }
    function place() {
      const t = triggerRef.current?.getBoundingClientRect()
      if (!t) return
      const margin = 8
      const width = Math.min(224, window.innerWidth - margin * 2)
      const left = Math.max(margin, Math.min(t.left, window.innerWidth - width - margin))
      setPos({ bottom: window.innerHeight - t.top + margin, left, width })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open])

  if (!activeOrg || memberships.length < 2) {
    return <p className="truncate text-sm font-medium text-foreground">{activeOrg?.organizations.name ?? 'No team'}</p>
  }

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => setOpen(!open)}
        aria-label={'Switch team, current team ' + activeOrg.organizations.name}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="flex w-full items-center gap-1 rounded text-sm font-medium text-foreground hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="truncate">{activeOrg.organizations.name}</span>
        <ChevronsUpDown className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden="true" />
      </button>
      {open && pos && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden="true" />
          <div
            role="listbox"
            aria-label="Your teams"
            style={{ position: 'fixed', bottom: pos.bottom, left: pos.left, width: pos.width }}
            className="z-50 rounded-lg border border-border bg-card py-1 shadow-xl"
          >
            {memberships.map(m => {
              const isActive = m.organization_id === activeOrg.organization_id
              return (
                <button
                  key={m.id}
                  role="option"
                  aria-selected={isActive}
                  onClick={() => { setActiveOrg(m); setOpen(false) }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                >
                  <span className="min-w-0 flex-1 truncate">{m.organizations.name}</span>
                  {isActive && <Check className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />}
                </button>
              )
            })}
          </div>
        </>
      )}
    </>
  )
}
